import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiFilterAccordion,
  MiFilterItem,
  MiIcon,
  MiInputTextbox,
  MiInternalDrawer,
  MiStackedTypography,
} from "@miplatform/components";
import { BUTTON_VARIANTS, ICONS_WEB } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import { Box, Divider, InputAdornment, Typography } from "@mui/material";
import { StatusChip } from "../../../../../components/StatusChip/StatusChip";
import { useImportPlanDrawerViewModel } from "./useImportPlanDrawerViewModel";

export function ImportPlanDrawer({
  open,
  onClose,
  stateManager,
  accountDetail,
}) {
  const {
    navigate,
    params: { tab, accountId, seriesOrCommunityId },
  } = useRouter();
  const {
    isLoading,
    search,
    filter,
    openResultsContainer,
    debouncedSearch,
    specPlans,
    seriesOptions,
    filteredSeriesOptions,
    filteredSpecLevelOptions,
    setSearch,
    getSeriesOptions,
    getSpecPlans,
    handleSearch,
    handleSeriesOptionFilter,
    handleSpecLevelOptionFilter,
    handleCloseResultContainer,
    handleCloseDrawer,
    handleImportPlan,
  } = useImportPlanDrawerViewModel({
    stateManager,
    onClose,
    navigate,
    tab,
    accountId,
    seriesOrCommunityId,
  });
  const { classes, theme } = useStyles();

  useEffect(() => {
    if (open) {
      getSeriesOptions();
    }
  }, [open]);

  useEffect(() => {
    if (seriesOptions.length > 0) {
      getSpecPlans();
    }
  }, [filter, debouncedSearch, seriesOptions]);

  return (
    <>
      <SearchResultsContainer
        open={openResultsContainer}
        onClose={handleCloseResultContainer}
        alertTitle="Click on the bid to import"
        alertDescription="Import the bid by clicking on the Configure button."
        storageKey="importPlanDrawer-alert"
        isLoading={isLoading}
        results={specPlans}
        renderResults={() =>
          specPlans.map((item) => (
            <PlanResultCard
              key={item.id}
              item={item}
              handleImportPlan={handleImportPlan}
            />
          ))
        }
      />
      <MiInternalDrawer
        title="Import Bid"
        open={open}
        onClose={handleCloseDrawer}
        removeHeaderMargin
      >
        <Box className={classes.planDrawerContainer}>
          <Box className={classes.searchContainer}>
            <MiInputTextbox
              className={classes.inputTextbox}
              placeholder="Search Bids"
              value={search}
              handleChange={(e) => handleSearch(e.target.value)}
              textfieldProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MiIcon path={mdi[ICONS_WEB.MAGNIFY]} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                    <MiIcon
                      path={mdi[ICONS_WEB.CLOSE]}
                      onClick={() => setSearch("")}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <MiFilterItem
            title="Customer"
            rightAction={
              <Typography className={classes.rightActionText}>
                {accountDetail.name} ({accountDetail.code})
                <MiIcon path={mdi[ICONS_WEB.LOCK_OUTLINE]} size={0.8} />
              </Typography>
            }
          />
          <MiFilterAccordion
            title="Series"
            selectedOptions={filter.series}
            showItemCount
          >
            {filteredSeriesOptions
              .filter(
                (option, index, self) =>
                  index === self.findIndex((o) => o.name === option.name)
              )
              .map((option) => {
                const isSelected = filter.series.includes(option.name);
                return (
                  <MiFilterItem
                    key={option.id}
                    title={option.name}
                    handleClick={() => handleSeriesOptionFilter(option)}
                    selected={isSelected}
                    iconPath={ICONS_WEB.CHECK}
                    iconColor={theme.palette.primary.main}
                    showIcon={isSelected}
                  />
                );
              })}
          </MiFilterAccordion>
          <MiFilterAccordion
            title="Spec Level"
            selectedOptions={filter.specLevel}
            showItemCount
          >
            {filteredSpecLevelOptions
              .filter(
                (option, index, self) =>
                  index ===
                  self.findIndex((o) => o.specLevel === option.specLevel)
              )
              .map((option) => {
                const isSelected = filter.specLevel.includes(option.specLevel);
                return (
                  <MiFilterItem
                    key={option.id}
                    keyVal={option.id}
                    title={option.specLevel}
                    handleClick={() => handleSpecLevelOptionFilter(option)}
                    selected={isSelected}
                    iconPath={ICONS_WEB.CHECK}
                    showIcon={isSelected}
                  />
                );
              })}
          </MiFilterAccordion>
        </Box>
      </MiInternalDrawer>
    </>
  );
}

function PlanResultCard({ item, handleImportPlan }) {
  const { classes, theme } = useStyles();

  return (
    <Box className={classes.resultCard}>
      <Box className={classes.infoContainer}>
        <MiStackedTypography
          title="Bid Name"
          description={item.name}
          descriptionProps={{
            fontWeight: theme.typography.fontWeightBold,
            color: `${theme.palette.primary.main} !important`,
          }}
        />
        <Box className={classes.detailContainer}>
          <MiStackedTypography
            title="Series"
            description={item.series || "--"}
          />
          <MiStackedTypography
            title="Spec Level"
            description={item.specLevel || "--"}
          />
          <MiStackedTypography
            title="Status"
            description={<StatusChip status={item.planStatus} />}
          />
        </Box>
      </Box>
      <Divider orientation="vertical" flexItem />
      <MiButton
        title="Add Bid"
        startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
        variant={BUTTON_VARIANTS.CONTAINED}
        size="small"
        onClick={() => handleImportPlan(item.id)}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  resultCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    width: "100%",
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    gap: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: `0px 2px 2px ${theme.palette.grey.accent}`,
  },
  infoContainer: {
    display: "flex",
    width: "100%",
    gap: theme.spacing(0.5),
    justifyContent: "space-between",
    alignItems: "center",
  },
  detailContainer: {
    display: "flex",
    gap: theme.spacing(5),
    flexWrap: "wrap",
  },
  rightActionText: {
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    color: theme.palette.grey.main,
    gap: theme.spacing(2),
  },
  planDrawerContainer: {
    height: "100%",
    width: 300,
  },
  searchContainer: {
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    top: 0,
    width: "100%",
    padding: theme.spacing(1, 3),
  },
  inputTextbox: {
    padding: theme.spacing(1, 0),
  },
}));
